import React from 'react';
import { amountToWordsRu } from '../../utils/rublesInWords';
import ipSeal from '../../assets/ip-seal.png';
import './InvoiceView.css';

const ENTITY_LABELS = { ip: 'ИП', ooo: 'ООО' };
const STATUS_LABELS = { pending: 'Ожидает оплаты', paid: 'Оплачен', canceled: 'Отменён' };
const PERIOD_LABELS = { month: '1 мес.', year: '1 год' };

const MONTHS_RU = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

// «01» сентября 2026 г. — традиционный формат даты на российском счёте,
// формат formatDate() из utils/formatters (01.09.2026) сюда не годится.
const formatInvoiceDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  return `«${day}» ${MONTHS_RU[d.getMonth()]} ${d.getFullYear()} г.`;
};

// Шаблон повторяет стандартный «образец счёта ИП» (см. Счет.docx): шапка
// поставщика, блок «образец заполнения платёжного поручения», счёт с
// продавцом/покупателем, таблица позиций, сумма прописью, подпись.
const InvoiceView = ({ invoice, platformRequisites, plan, onClose }) => {
  if (!invoice) return null;
  let payer = {};
  try {
    payer = JSON.parse(invoice.requisites_snapshot || '{}');
  } catch (e) {
    payer = {};
  }

  const p = platformRequisites || {};
  const periodLabel = plan ? (PERIOD_LABELS[plan.billing_period] || plan.billing_period) : '';
  const itemName = `Подписка «${plan?.name || `тариф #${invoice.plan_id}`}»${periodLabel ? ` (${periodLabel})` : ''}${invoice.promo_code ? `, промокод ${invoice.promo_code}` : ''}`;

  return (
    <div className="ui-modal invoice-view-overlay" onClick={onClose} role="presentation">
      <div className="ui-modal-box wide invoice-view-box" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="ui-modal-head no-print">
          <h2>Счёт № {invoice.number}</h2>
          <button className="ui-icon-btn" type="button" onClick={onClose} aria-label="Закрыть">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M19.0068 5L5.00684 19" /><path d="M19 19L5 5" />
            </svg>
          </button>
        </div>

        <div className="invoice-print">
          <div className="invoice-supplier-head">
            <div>{p.entity_type} {p.full_name}</div>
            {p.address && <div>{p.address}</div>}
            {(p.email || p.phone) && <div>{[p.email, p.phone].filter(Boolean).join(', ')}</div>}
          </div>

          <div className="invoice-sample-title">Образец заполнения платёжного поручения</div>

          <table className="invoice-bank-table">
            <colgroup>
              <col style={{ width: '55%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '30%' }} />
            </colgroup>
            <tbody>
              <tr>
                <td className="invoice-bank-name">{p.bank_name}</td>
                <td className="invoice-bank-label">БИК</td>
                <td className="invoice-bank-value">{p.bik}</td>
              </tr>
              <tr>
                <td />
                <td className="invoice-bank-label">Сч. №</td>
                <td className="invoice-bank-value">{p.correspondent_account}</td>
              </tr>
              <tr>
                <td colSpan={3} className="invoice-bank-caption">Банк получателя</td>
              </tr>
              <tr>
                <td className="invoice-bank-inn-kpp">
                  ИНН {p.inn}{p.ogrnip ? ` ОГРНИП ${p.ogrnip}` : ''}
                </td>
                <td className="invoice-bank-label">Сч. №</td>
                <td className="invoice-bank-value">{p.checking_account}</td>
              </tr>
              <tr>
                <td colSpan={3}>{p.entity_type} {p.full_name}</td>
              </tr>
              <tr>
                <td colSpan={3} className="invoice-bank-caption">Получатель</td>
              </tr>
            </tbody>
          </table>

          <h1 className="invoice-title">Счёт № {invoice.number} от {formatInvoiceDate(invoice.created_at)}</h1>

          <table className="invoice-parties-table">
            <tbody>
              <tr>
                <td className="invoice-parties-label">Поставщик:</td>
                <td>{p.entity_type} {p.full_name}, ИНН {p.inn}{p.ogrnip ? `, ОГРНИП ${p.ogrnip}` : ''}</td>
              </tr>
              <tr>
                <td className="invoice-parties-label">Покупатель:</td>
                <td>
                  {ENTITY_LABELS[payer.entity_type] || payer.entity_type} {payer.company_name},
                  {' '}ИНН {payer.inn}{payer.kpp ? `, КПП ${payer.kpp}` : ''}
                </td>
              </tr>
            </tbody>
          </table>

          <table className="invoice-items">
            <thead>
              <tr>
                <th className="invoice-items-num">№</th>
                <th>Товары (работы, услуги)</th>
                <th className="invoice-items-num">Кол-во</th>
                <th className="invoice-items-num">Ед.</th>
                <th className="invoice-items-sum">Цена</th>
                <th className="invoice-items-sum">Сумма</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="invoice-items-num">1</td>
                <td>{itemName}</td>
                <td className="invoice-items-num">1</td>
                <td className="invoice-items-num">усл.</td>
                <td className="invoice-items-sum">{invoice.amount}</td>
                <td className="invoice-items-sum">{invoice.amount}</td>
              </tr>
            </tbody>
          </table>
          <div className="invoice-vat-note">НДС не облагается (УСН)</div>

          <p className="invoice-total-words">
            Всего наименований 1 на сумму {invoice.amount} {invoice.currency}.<br />
            Всего к оплате: {amountToWordsRu(invoice.amount)}.
          </p>

          <div className="invoice-signature-wrap">
            <table className="invoice-signature-table">
              <tbody>
                <tr>
                  <td className="invoice-parties-label">Поставщик</td>
                  <td>{p.entity_type === 'ИП' ? 'Индивидуальный предприниматель' : p.entity_type}</td>
                  <td className="invoice-signature-line" />
                  <td className="invoice-signature-line" />
                  <td>{p.full_name}</td>
                </tr>
                <tr className="invoice-signature-caption">
                  <td />
                  <td>должность</td>
                  <td>подпись</td>
                  <td />
                  <td>расшифровка подписи</td>
                </tr>
              </tbody>
            </table>
            {/* Печать — поверх подписи и частично «М.П.», как на реальном оттиске */}
            <img src={ipSeal} alt="Печать ИП" className="invoice-seal" />
          </div>

          <div className="invoice-status no-print">
            Статус: <span className={`ui-status ${invoice.status === 'paid' ? 'on' : invoice.status === 'canceled' ? 'off' : ''}`}>
              {STATUS_LABELS[invoice.status] || invoice.status}
            </span>
          </div>

          <p className="ui-dim invoice-note">
            Оплата подтверждается вручную после поступления средств на расчётный счёт —
            зачисление обычно занимает 1-3 рабочих дня с момента отправки платёжного поручения.
          </p>
        </div>

        <div className="ui-modal-actions no-print">
          <button type="button" className="ui-btn-primary" onClick={() => window.print()}>Печать / Сохранить PDF</button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceView;
